import { Link } from "react-router-dom";
import { useCompany, fmtNipc } from "../../context/CompanyContext";

/**
 * Política de Proteção de Dados (RGPD/GDPR) — texto base, redigido para
 * uma plataforma SaaS de gestão de alojamento local que processa dados
 * de clientes (hosts) e funcionários. Os campos entre [] devem ser
 * preenchidos antes da publicação definitiva.
 */
export default function Privacidade() {
  const { company } = useCompany();
  const updated = "Abril de 2026";
  return (
    <div className="min-h-screen bg-[#041f1b] text-[#fdfbf7]" data-testid="legal-privacidade-page">
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24 space-y-8">
        <header className="space-y-3">
          <Link to="/" className="text-[10px] uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37]">
            ← Voltar à página inicial
          </Link>
          <div className="eyebrow">Documento legal · RGPD</div>
          <h1 className="font-display text-4xl sm:text-5xl">Política de Proteção de Dados</h1>
          <p className="text-xs text-[#a0b2af]">Última atualização: {updated}</p>
        </header>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">1. Responsável pelo tratamento</h2>
          <p>
            O responsável pelo tratamento dos dados pessoais é
            {" "}<strong className="text-[#fdfbf7]" data-testid="privacy-legal-name">{company.legal_name}</strong>,
            com sede em <strong className="text-[#fdfbf7]">{[company.address, company.postal_code, company.city].filter(Boolean).join(", ")} — {company.country}</strong>
            {company.nipc && <>, NIPC <strong className="text-[#fdfbf7]">{fmtNipc(company.nipc)}</strong></>}. Para qualquer questão de
            privacidade, contacte-nos em
            {" "}<a href={`mailto:${company.email}`} className="text-[#d4af37] hover:underline">
              {company.email}
            </a>.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">2. Dados recolhidos</h2>
          <p>Recolhemos apenas os dados estritamente necessários para prestar o serviço:</p>
          <ul className="list-disc list-inside space-y-1.5">
            <li><strong className="text-[#fdfbf7]">Identificação e contacto:</strong> nome, email, número de telefone, NIF (quando aplicável a faturação).</li>
            <li><strong className="text-[#fdfbf7]">Conta e autenticação:</strong> nome de utilizador, password (armazenada cifrada com bcrypt), tokens de sessão.</li>
            <li><strong className="text-[#fdfbf7]">Dados operacionais:</strong> apartamentos geridos, reservas, limpezas, fotos de relatórios, comentários e avaliações.</li>
            <li><strong className="text-[#fdfbf7]">Dados de faturação:</strong> nome fiscal, NIF, morada, histórico de pagamentos.</li>
            <li><strong className="text-[#fdfbf7]">Dados técnicos:</strong> endereço IP, user-agent do browser, registos de acesso, métricas de utilização.</li>
          </ul>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">3. Finalidades do tratamento</h2>
          <ul className="list-disc list-inside space-y-1.5">
            <li>Prestação e gestão do serviço contratado (agendamento, faturação, comunicação).</li>
            <li>Cumprimento de obrigações legais (faturação eletrónica, contabilidade, fiscais).</li>
            <li>Comunicações operacionais e de suporte ao utilizador.</li>
            <li>Prevenção de fraude, abuso e segurança da plataforma.</li>
            <li>Melhoria do serviço através de análise agregada e anonimizada de utilização.</li>
          </ul>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">4. Base legal (RGPD art. 6.º)</h2>
          <ul className="list-disc list-inside space-y-1.5">
            <li><strong className="text-[#fdfbf7]">Execução de contrato</strong> — para a prestação do serviço solicitado pelo utilizador.</li>
            <li><strong className="text-[#fdfbf7]">Cumprimento de obrigação legal</strong> — para faturação, contabilidade e dever fiscal.</li>
            <li><strong className="text-[#fdfbf7]">Interesse legítimo</strong> — para segurança, prevenção de fraude e melhoria do serviço.</li>
            <li><strong className="text-[#fdfbf7]">Consentimento</strong> — para comunicações de marketing e instalação de cookies não essenciais (revogável a qualquer momento).</li>
          </ul>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">5. Direitos do utilizador</h2>
          <p>Nos termos do RGPD, o utilizador tem direito a:</p>
          <ul className="list-disc list-inside space-y-1.5">
            <li><strong className="text-[#fdfbf7]">Acesso</strong> — saber que dados detemos sobre si.</li>
            <li><strong className="text-[#fdfbf7]">Retificação</strong> — corrigir dados inexatos ou incompletos.</li>
            <li><strong className="text-[#fdfbf7]">Apagamento</strong> &laquo;direito ao esquecimento&raquo; — solicitar a eliminação dos seus dados.</li>
            <li><strong className="text-[#fdfbf7]">Limitação</strong> do tratamento em determinadas circunstâncias.</li>
            <li><strong className="text-[#fdfbf7]">Portabilidade</strong> — receber os seus dados em formato estruturado e legível por máquina.</li>
            <li><strong className="text-[#fdfbf7]">Oposição</strong> ao tratamento baseado em interesse legítimo ou marketing.</li>
            <li><strong className="text-[#fdfbf7]">Retirar o consentimento</strong> a qualquer momento, sem afetar a licitude do tratamento anterior.</li>
            <li><strong className="text-[#fdfbf7]">Reclamar</strong> junto da Comissão Nacional de Proteção de Dados (CNPD — <a href="https://www.cnpd.pt" target="_blank" rel="noreferrer" className="text-[#d4af37] hover:underline">www.cnpd.pt</a>).</li>
          </ul>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">6. Como exercer os seus direitos</h2>
          <p>
            Pode exercer qualquer dos direitos acima enviando email para
            {" "}<a href="mailto:luxstaymadeira@gmail.com" className="text-[#d4af37] hover:underline">
              luxstaymadeira@gmail.com
            </a>{" "}
            indicando o pedido pretendido. Responderemos no prazo máximo de 30 dias (prorrogável por
            mais 60 dias em caso de pedidos complexos, conforme art. 12.º RGPD). Para garantir a
            segurança dos dados, poderemos solicitar comprovativo de identidade.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">7. Prazos de retenção</h2>
          <ul className="list-disc list-inside space-y-1.5">
            <li><strong className="text-[#fdfbf7]">Conta ativa:</strong> enquanto a conta existir e for utilizada.</li>
            <li><strong className="text-[#fdfbf7]">Dados contabilísticos e fiscais:</strong> 10 anos (obrigação legal — art. 123.º CIRC).</li>
            <li><strong className="text-[#fdfbf7]">Logs técnicos e segurança:</strong> 12 meses.</li>
            <li><strong className="text-[#fdfbf7]">Após encerramento da conta:</strong> dados não obrigatórios são apagados em até 90 dias; obrigatórios mantêm-se pelo prazo legal aplicável.</li>
          </ul>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">8. Subcontratantes (terceiros)</h2>
          <p>
            Para prestar o serviço, partilhamos dados estritamente necessários com subcontratantes
            sujeitos a contrato de processamento de dados (RGPD art. 28.º):
          </p>
          <ul className="list-disc list-inside space-y-1.5">
            <li><strong className="text-[#fdfbf7]">Stripe Inc. / Stripe Payments Europe</strong> — processamento de pagamentos.</li>
            <li><strong className="text-[#fdfbf7]">Resend (Resend Inc.)</strong> — envio de emails transacionais.</li>
            <li><strong className="text-[#fdfbf7]">MongoDB Atlas</strong> — armazenamento de base de dados (UE).</li>
            <li><strong className="text-[#fdfbf7]">OpenAI / Google / Anthropic</strong> — análise de texto e OCR de faturas (apenas dados não sensíveis necessários ao processamento).</li>
            <li><strong className="text-[#fdfbf7]">Cloudflare / Kubernetes hosting</strong> — infraestrutura, CDN e proteção contra fraude.</li>
          </ul>
          <p>
            Sempre que possível, os dados são processados em servidores dentro do Espaço Económico
            Europeu. Para transferências internacionais, recorremos a Cláusulas Contratuais-Tipo da
            Comissão Europeia ou outras garantias adequadas previstas no RGPD.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">9. Medidas de segurança</h2>
          <ul className="list-disc list-inside space-y-1.5">
            <li>Comunicações cifradas em trânsito (HTTPS/TLS).</li>
            <li>Passwords armazenadas com hashing forte (bcrypt) — nunca em texto claro.</li>
            <li>Controlo de acesso baseado em papéis (admin, cliente, funcionário).</li>
            <li>Cookies httpOnly + tokens de sessão fingerprintados ao dispositivo.</li>
            <li>Backups regulares e monitorização contínua de incidentes.</li>
            <li>Pseudonimização e minimização sempre que tecnicamente viável.</li>
          </ul>
          <p>
            Em caso de violação de dados pessoais que represente risco elevado para os direitos do
            utilizador, será notificado conforme art. 34.º RGPD, juntamente com a CNPD.
          </p>
        </section>

        <section className="space-y-3 text-sm leading-relaxed text-[#a0b2af]">
          <h2 className="font-display text-xl text-[#fdfbf7]">10. Alterações</h2>
          <p>
            Esta política pode ser atualizada para refletir alterações legais ou do serviço. A versão
            mais recente está sempre disponível em <Link to="/privacidade" className="text-[#d4af37] hover:underline">/privacidade</Link>.
            Alterações materiais serão comunicadas com aviso prévio razoável.
          </p>
        </section>

        <footer className="border-t border-[#1a4640] pt-6 text-xs text-[#a0b2af] flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} · {company.legal_name}</span>
          <Link to="/termos" className="text-[#d4af37] hover:underline">
            ← Ver Termos &amp; Condições
          </Link>
        </footer>
      </main>
    </div>
  );
}
